import { useState, useEffect } from 'react';
import { X, Cookie, Shield, BarChart } from 'lucide-react';
import { secureStorage } from '../lib/security-utils';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
  timestamp: number;
}

const COOKIE_CONSENT_KEY = 'mentra_cookie_consent';
const COOKIE_PREFERENCES_KEY = 'mentra_cookie_preferences';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    functional: false,
    marketing: false,
    timestamp: Date.now(),
  });

  useEffect(() => {
    const consent = secureStorage.getItem(COOKIE_CONSENT_KEY);

    if (!consent) {
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      const savedPreferences = secureStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (savedPreferences) {
        try {
          const parsed = JSON.parse(savedPreferences);
          setPreferences(parsed);
          applyPreferences(parsed);
        } catch (error) {
          console.error('Failed to parse cookie preferences:', error);
        }
      }
    }
  }, []);

  const applyPreferences = (prefs: CookiePreferences) => {
    if (!prefs.analytics) {
      if (typeof window.gtag !== 'undefined') {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
        });
      }
    }

    if (!prefs.marketing) {
      if (typeof window.gtag !== 'undefined') {
        window.gtag('consent', 'update', {
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        });
      }
    }
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true,
      timestamp: Date.now(),
    };

    saveConsent(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
      timestamp: Date.now(),
    };

    saveConsent(necessaryOnly);
  };

  const saveCustomPreferences = () => {
    const customPreferences = {
      ...preferences,
      necessary: true,
      timestamp: Date.now(),
    };

    saveConsent(customPreferences);
  };

  const saveConsent = (prefs: CookiePreferences) => {
    secureStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    secureStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    applyPreferences(prefs);
    setShowBanner(false);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'necessary' || key === 'timestamp') return;
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#E0EBE9] rounded-lg">
                <Cookie className="w-6 h-6 text-[#8CAFA8]" />
              </div>
              <h2 className="text-xl font-semibold text-[#1F2937]">Cookie Preferences</h2>
            </div>
            <button
              onClick={acceptNecessary}
              className="text-[#D1D5DB] hover:text-[#6B7280] transition-colors"
              aria-label="Close and accept necessary cookies only"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-[#6B7280] mb-6">
            We use cookies to enhance your experience, analyze site traffic, and personalize
            content. You can customize your preferences or accept all cookies.
          </p>

          {!showDetails ? (
            <div className="space-y-3">
              <button
                onClick={acceptAll}
                className="w-full bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition-colors"
              >
                Accept All Cookies
              </button>

              <button
                onClick={acceptNecessary}
                className="w-full bg-gray-200 text-[#4B5563] px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Accept Necessary Only
              </button>

              <button
                onClick={() => setShowDetails(true)}
                className="w-full text-[#8CAFA8] px-6 py-3 rounded-lg font-semibold hover:bg-[#F0F4F3] transition-colors"
              >
                Customize Preferences
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-[#1F2937]">Necessary Cookies</h3>
                  </div>
                  <span className="text-sm font-medium text-green-600 bg-green-100 px-3 py-1 rounded-full">
                    Always Active
                  </span>
                </div>
                <p className="text-sm text-[#6B7280]">
                  Essential for the website to function properly. These cookies enable core
                  functionality such as security, authentication, and accessibility features.
                </p>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <BarChart className="w-5 h-5 text-[#8CAFA8]" />
                    <h3 className="font-semibold text-[#1F2937]">Analytics Cookies</h3>
                  </div>
                  <button
                    onClick={() => togglePreference('analytics')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.analytics ? 'bg-[#8CAFA8]' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={preferences.analytics}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.analytics ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-[#6B7280]">
                  Help us understand how visitors interact with our website by collecting and
                  reporting information anonymously.
                </p>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Cookie className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-[#1F2937]">Functional Cookies</h3>
                  </div>
                  <button
                    onClick={() => togglePreference('functional')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.functional ? 'bg-[#8CAFA8]' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={preferences.functional}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.functional ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-[#6B7280]">
                  Enable enhanced functionality and personalization, such as videos and live chat.
                </p>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <BarChart className="w-5 h-5 text-orange-600" />
                    <h3 className="font-semibold text-[#1F2937]">Marketing Cookies</h3>
                  </div>
                  <button
                    onClick={() => togglePreference('marketing')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.marketing ? 'bg-[#8CAFA8]' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={preferences.marketing}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.marketing ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-[#6B7280]">
                  Used to track visitors across websites to display relevant advertisements.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={saveCustomPreferences}
                  className="flex-1 bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition-colors"
                >
                  Save Preferences
                </button>
                <button
                  onClick={() => setShowDetails(false)}
                  className="flex-1 bg-gray-200 text-[#4B5563] px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>
              </div>

              <p className="text-xs text-[#9CA3AF] text-center">
                Learn more in our{' '}
                <a href="/privacy" className="text-[#8CAFA8] hover:underline">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="/cookies" className="text-[#8CAFA8] hover:underline">
                  Cookie Policy
                </a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
